<template>
  <div class="content-area">
    <div
      v-if="uiStore.isCostsSelected && projectStore.costs"
      ref="contentBody"
      class="content-body"
      @scroll="handleScroll"
    >
      <CostReport :costs="projectStore.costs" />
    </div>
    <div
      v-else-if="outputKind === 'chapter-notes' && currentOutputContent"
      ref="contentBody"
      class="content-body"
      @scroll="handleScroll"
    >
      <ChapterNotesView :content="currentOutputContent" />
      <div class="component-feedback">
        <FeedbackWidget
          v-if="projectStore.isComplete && selectedFrameworkId && selectedOutputName"
          :projectId="projectStore.projectId"
          :frameworkId="selectedFrameworkId"
          :outputName="selectedOutputName"
          :key="`${selectedFrameworkId}:${selectedOutputName}`"
        />
      </div>
    </div>
    <div
      v-else-if="outputKind === 'revision-plan' && currentOutputContent"
      ref="contentBody"
      class="content-body"
      @scroll="handleScroll"
    >
      <RevisionPlanView
        :content="currentOutputContent"
        :projectId="projectStore.projectId"
        :frameworkId="selectedFrameworkId ?? ''"
      />
      <div class="component-feedback">
        <FeedbackWidget
          v-if="projectStore.isComplete && selectedFrameworkId && selectedOutputName"
          :projectId="projectStore.projectId"
          :frameworkId="selectedFrameworkId"
          :outputName="selectedOutputName"
          :key="`${selectedFrameworkId}:${selectedOutputName}`"
        />
      </div>
    </div>
    <div
      v-else-if="currentOutputContent && uiStore.selectedTitle"
      ref="contentBody"
      class="content-body"
      @scroll="handleScroll"
    >
      <div class="document-card">
        <div class="rich-text" ref="richTextRef"></div>
        <FeedbackWidget
          v-if="projectStore.isComplete && selectedFrameworkId && selectedOutputName"
          :projectId="projectStore.projectId"
          :frameworkId="selectedFrameworkId"
          :outputName="selectedOutputName"
          :key="`${selectedFrameworkId}:${selectedOutputName}`"
        />
      </div>
    </div>
    <div v-else-if="uiStore.selectedTitle && uiStore.isSelectedOutputStreaming" class="streaming-content">
      <div class="streaming-icon">
        <QIcon :path="mdiLoading" :size="48" color="var(--color-accent)" />
      </div>
      <h2>{{ uiStore.selectedTitle.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase()) }}</h2>
      <p>This document is being generated. Content will appear here as it streams in...</p>
    </div>
    <DashboardView v-else-if="projectStore.isComplete" />
    <div v-else class="empty-content">
      <QIcon :path="mdiFileDocumentOutline" :size="48" color="var(--color-text-muted)" />
      <h2>{{ projectStore.status === 'not_found' ? 'Edit Not Found' : 'Welcome to Your AI Developmental Edit' }}</h2>
      <p v-if="projectStore.status === 'not_found'">This edit does not exist or may have been deleted.</p>
      <template v-else>
        <p>Select a deliverable from the sidebar to view its content.</p>
        <p class="empty-subtitle">Your completed developmental edits will appear here once processing is finished.</p>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { mdiFileDocumentOutline, mdiLoading } from '@mdi/js';
import morph from 'morphdom';
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { SVG_CHECK, SVG_COPY, injectShareCards, parseHighlightsJson, parseMarkdown } from '../lib/renderOutput';
import { useProjectStore } from '../stores/project';
import { useUIStore } from '../stores/ui';
import ChapterNotesView from './ChapterNotesView.vue';
import CostReport from './CostReport.vue';
import DashboardView from './DashboardView.vue';
import FeedbackWidget from './FeedbackWidget.vue';
import QIcon from './QIcon.vue';
import RevisionPlanView from './RevisionPlanView.vue';

const uiStore = useUIStore();
const projectStore = useProjectStore();

const contentBody = ref<HTMLElement>();
const richTextRef = ref<HTMLElement>();
const previousSelectedItem = ref<string | null>(null);

const selectedFrameworkId = computed(() => {
  if (!uiStore.selectedItem || !uiStore.selectedItem.includes(':')) return null;
  return uiStore.selectedItem.split(':')[0];
});

const selectedOutputName = computed(() => {
  if (!uiStore.selectedItem || !uiStore.selectedItem.includes(':')) return null;
  return uiStore.selectedItem.split(':')[1];
});

// Chapter notes and revision plans render through dedicated Vue components
// (parsed data + keyed templates); everything else goes through the
// morphdom/rich-text path.
const outputKind = computed<'chapter-notes' | 'revision-plan' | 'generic'>(() => {
  const name = selectedOutputName.value ?? '';
  if (name.endsWith('_chapter_notes')) return 'chapter-notes';
  if (name.endsWith('_revision_plan')) return 'revision-plan';
  return 'generic';
});

// Look up the highlights companion output for the current framework (hidden from sidebar, shown inline)
const inlineHighlightsContent = computed(() => {
  if (!selectedFrameworkId.value || !projectStore.isComplete) return null;
  const framework = projectStore.frameworks.find(f => f.frameworkId === selectedFrameworkId.value);
  if (!framework) return null;
  const highlights = framework.outputs.find(o => o.name.endsWith('_highlights'));
  return highlights?.content || null;
});

function openShareUrl(platform: string | null, quote: string) {
  const MAX_X_QUOTE = 200;
  const truncated = quote.length > MAX_X_QUOTE ? quote.slice(0, MAX_X_QUOTE - 1) + '…' : quote;
  const url = window.location.href;
  const baseText = (q: string) => `"${q}"\n\nMy manuscript just got roasted by Twig.`;

  switch (platform) {
    case 'x':
      window.open(
        `https://twitter.com/intent/tweet?text=${encodeURIComponent(baseText(truncated))}&url=${encodeURIComponent(url)}`,
        '_blank',
        'noopener,noreferrer'
      );
      break;
    case 'threads':
      window.open(
        `https://www.threads.net/intent/post?text=${encodeURIComponent(`${baseText(quote)}\n${url}`)}`,
        '_blank',
        'noopener,noreferrer'
      );
      break;
    case 'facebook':
      window.open(
        `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(baseText(quote))}`,
        '_blank',
        'noopener,noreferrer'
      );
      break;
    case 'linkedin':
      window.open(
        `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
        '_blank',
        'noopener,noreferrer'
      );
      break;
    case 'copy':
      navigator.clipboard.writeText(`${baseText(quote)}\n${url}`);
      break;
  }
}

function handleShareClick(e: MouseEvent) {
  const btn = (e.target as Element).closest('[data-platform]') as HTMLElement | null;
  if (!btn) return;
  e.stopPropagation();
  const platform = btn.getAttribute('data-platform');
  const card = btn.closest('[data-share-card]') as HTMLElement | null;
  const quote = card?.getAttribute('data-share-quote') ?? '';
  openShareUrl(platform, quote);

  if (platform === 'copy') {
    btn.innerHTML = SVG_CHECK;
    btn.classList.add('copied');
    setTimeout(() => {
      btn.innerHTML = SVG_COPY;
      btn.classList.remove('copied');
    }, 1500);
  }
}

// Computed property to get current file content from the store
const currentOutputContent = computed(() => {
  // If there's a selected item, try to get its content from the store
  if (uiStore.selectedItem) {
    // Check if it's a special item
    if (uiStore.selectedItem === 'manuscript') {
      return projectStore.manuscript;
    } else if (uiStore.selectedItem === 'costs') {
      return JSON.stringify(projectStore.costs, null, 2);
    }

    // Otherwise, get from frameworks
    const [frameworkId, fileName] = uiStore.selectedItem.split(':');
    const framework = projectStore.frameworks.find(f => f.frameworkId === frameworkId);
    if (framework) {
      const output = framework.outputs.find(o => o.name === fileName);
      if (output) {
        return output.content || null;
      }
    }
  }

  // Fall back to selectedContent if nothing found
  return uiStore.selectedContent;
});

// Auto-scroll state management
const isScrolledToBottom = ref<boolean>(true);

// Helper to check if user is scrolled to bottom (within 10px tolerance)
function checkIfScrolledToBottom(): boolean {
  if (!contentBody.value) return false;
  const { scrollTop, scrollHeight, clientHeight } = contentBody.value;
  return scrollTop + clientHeight >= scrollHeight - 10;
}

// Session storage key for scroll position
const getScrollStorageKey = (item: string) => `scroll_${projectStore.projectId}_${item}`;

// Save scroll position
function saveScrollPosition() {
  if (contentBody.value && uiStore.selectedItem) {
    const scrollTop = contentBody.value.scrollTop;
    try {
      sessionStorage.setItem(getScrollStorageKey(uiStore.selectedItem), scrollTop.toString());
    } catch (e) {
      console.warn('Failed to save scroll position:', e);
    }
  }
}

// Load scroll position
function loadScrollPosition() {
  if (contentBody.value && uiStore.selectedItem) {
    try {
      const stored = sessionStorage.getItem(getScrollStorageKey(uiStore.selectedItem));
      if (stored) {
        const scrollTop = parseInt(stored, 10);
        contentBody.value.scrollTop = scrollTop;
      } else {
        // New file, scroll to top
        contentBody.value.scrollTop = 0;
      }
    } catch (e) {
      console.warn('Failed to load scroll position:', e);
      if (contentBody.value) {
        contentBody.value.scrollTop = 0;
      }
    }
  }
}

// Function to update content with morphdom
async function updateContent() {
  // If a highlights output is selected directly, skip rendering (it's internal data)
  if (selectedOutputName.value?.endsWith('_highlights')) return;

  // Component-rendered outputs manage their own DOM; morphdom must not touch them
  if (outputKind.value !== 'generic') return;

  if (!currentOutputContent.value || !uiStore.selectedItem || !richTextRef.value) {
    return;
  }

  // Check if we're switching files or this is the initial load
  const isSwitchingFiles = previousSelectedItem.value && previousSelectedItem.value !== uiStore.selectedItem;
  const isInitialLoad = !previousSelectedItem.value;

  if (isSwitchingFiles && contentBody.value) {
    // Save previous scroll position before switching
    const prevScrollTop = contentBody.value.scrollTop;
    try {
      sessionStorage.setItem(getScrollStorageKey(previousSelectedItem.value!), prevScrollTop.toString());
    } catch (e) {
      console.warn('Failed to save previous scroll position:', e);
    }
  }

  // Check if user was scrolled to bottom before content update (not for file switches or initial load)
  const wasScrolledToBottom = !isSwitchingFiles && !isInitialLoad && checkIfScrolledToBottom();

  // Parse markdown and inject inline share cards if highlights exist
  let newHTML = parseMarkdown(currentOutputContent.value);
  if (inlineHighlightsContent.value) {
    const highlights = parseHighlightsJson(inlineHighlightsContent.value);
    if (highlights.length > 0) newHTML = injectShareCards(newHTML, highlights);
  }

  // Update content using morphdom for efficient diffing
  try {
    if (isInitialLoad || richTextRef.value.innerHTML === '') {
      // Initial load or empty content - just set innerHTML directly
      richTextRef.value.innerHTML = newHTML;
    } else {
      // Use morphdom to efficiently update only changed content
      // Create a temporary container with just the content, not the wrapper
      const tempContainer = document.createElement('div');
      tempContainer.innerHTML = newHTML;

      // Morph only the content, not the outer div with the class
      morph(richTextRef.value, tempContainer, {
        childrenOnly: true,
      });
    }
  } catch (error) {
    console.warn('Morphdom failed, falling back to innerHTML:', error);
    richTextRef.value.innerHTML = newHTML;
  }

  // Handle scroll position
  await nextTick();

  if (isSwitchingFiles || isInitialLoad) {
    // New file or initial load - load saved scroll position or scroll to top
    loadScrollPosition();
    isScrolledToBottom.value = checkIfScrolledToBottom();
  } else if (wasScrolledToBottom) {
    // Content update - auto-scroll to bottom if user was already there
    if (contentBody.value) {
      contentBody.value.scrollTop = contentBody.value.scrollHeight;
      isScrolledToBottom.value = true;
    }
  }

  previousSelectedItem.value = uiStore.selectedItem;
}

// Watch for content changes and handle morphdom diffing + scroll management
watch([currentOutputContent, inlineHighlightsContent, () => uiStore.selectedItem, richTextRef], updateContent);

// Scroll management for component-rendered outputs (chapter notes, revision
// plan): morphdom's updateContent() is skipped for these, so restore the saved
// position on file switch and stick to bottom during streaming here instead.
watch([currentOutputContent, () => uiStore.selectedItem], async () => {
  if (outputKind.value === 'generic') return;

  const isSwitchingFiles = previousSelectedItem.value && previousSelectedItem.value !== uiStore.selectedItem;
  const isInitialLoad = !previousSelectedItem.value;
  const wasScrolledToBottom = !isSwitchingFiles && !isInitialLoad && checkIfScrolledToBottom();

  await nextTick();

  if (isSwitchingFiles || isInitialLoad) {
    loadScrollPosition();
    isScrolledToBottom.value = checkIfScrolledToBottom();
  } else if (wasScrolledToBottom && contentBody.value) {
    contentBody.value.scrollTop = contentBody.value.scrollHeight;
    isScrolledToBottom.value = true;
  }

  previousSelectedItem.value = uiStore.selectedItem;
});

// Delegated click handler for editorial section toggles
function handleSectionToggle(e: MouseEvent) {
  const toggle = (e.target as Element).closest('[data-editorial-toggle]');
  if (!toggle) return;
  const section = toggle.closest('[data-editorial-section]');
  if (!section) return;
  section.classList.toggle('is-collapsed');
  toggle.querySelector('.section-chevron')?.classList.toggle('collapsed');
}

onMounted(() => {
  richTextRef.value?.addEventListener('click', handleSectionToggle);
  richTextRef.value?.addEventListener('click', handleShareClick);
});

onUnmounted(() => {
  richTextRef.value?.removeEventListener('click', handleSectionToggle);
  richTextRef.value?.removeEventListener('click', handleShareClick);
});

// Re-attach listeners after morphdom replaces content
watch(richTextRef, (el, prev) => {
  prev?.removeEventListener('click', handleSectionToggle);
  prev?.removeEventListener('click', handleShareClick);
  el?.addEventListener('click', handleSectionToggle);
  el?.addEventListener('click', handleShareClick);
});

// Save scroll position on scroll and track if user is at bottom
function handleScroll() {
  if (uiStore.selectedItem) {
    saveScrollPosition();
    // Update scroll-to-bottom state
    isScrolledToBottom.value = checkIfScrolledToBottom();
  }
}
</script>

<style scoped>
.content-area {
  height: 100%;
  background: var(--color-bg-primary);
  display: flex;
  flex-direction: column;
  position: relative;
}

.content-body {
  flex: 1;
  padding: 2rem 2rem 12rem;
  overflow-y: auto;
  background: linear-gradient(180deg, var(--color-bg-secondary) 0%, var(--color-bg-tertiary) 100%);
}

/* Reduce content body padding on smaller screens */
@media (max-width: 768px) {
  .content-body {
    padding: 0.5rem; /* Half of 1rem */
  }
}

.document-card {
  max-width: 816px;
  margin: 0 auto;
  background: white;
  border-radius: var(--border-radius);
  box-shadow: var(--shadow-lg);
}

.component-feedback {
  max-width: 816px;
  margin: 1rem auto 0;
}

@media (max-width: 1040px) {
  .document-card {
    box-shadow: var(--shadow-md);
  }
}

.rich-text {
  min-height: 1056px;
  font-family: 'PT Serif', serif;
  font-size: 16px;
  line-height: 1.75;
  color: var(--color-text-primary);
  padding: 72px;
}

/* Reduce padding by half when content doesn't fit fully */
@media (max-width: 1040px) {
  .rich-text {
    padding: 48px;
  }
}

/* Further reduce padding for smaller screens */
@media (max-width: 768px) {
  .rich-text {
    padding: 24px; /* Quarter of 96px for mobile */
  }
}

/* Extra small screens - minimal padding */
@media (max-width: 480px) {
  .rich-text {
    padding: 16px; /* Minimal padding for very small screens */
  }
}

.rich-text :deep(h1) {
  font-size: 2rem;
  font-weight: 700;
  margin: 2rem 0 1.5rem 0;
  color: var(--color-text-primary);
  line-height: 1.2;
}

.rich-text :deep(h1:first-child) {
  margin-top: 0;
}

.rich-text :deep(h2) {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 1.75rem 0 1rem 0;
  color: var(--color-text-primary);
  line-height: 1.3;
}

.rich-text :deep(h3) {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 1.5rem 0 0.75rem 0;
  color: var(--color-text-primary);
  line-height: 1.4;
}

.rich-text :deep(p) {
  margin-bottom: 1.25rem;
}

.rich-text :deep(strong) {
  font-weight: 600;
}

.rich-text :deep(em) {
  font-style: italic;
}

.rich-text :deep(code) {
  font-size: 0.875rem;
  background: var(--color-bg-tertiary);
  padding: 2px 4px;
  border-radius: 3px;
  color: var(--color-text-primary);
}

.rich-text :deep(pre) {
  background: var(--color-bg-tertiary);
  padding: var(--spacing-md);
  border-radius: var(--border-radius);
  overflow-x: auto;
  margin: 1.5rem 0;
}

.rich-text :deep(pre code) {
  background: none;
  padding: 0;
}

.rich-text :deep(blockquote) {
  border-left: 3px solid var(--color-border-secondary);
  padding-left: var(--spacing-lg);
  margin: 1.5rem 0;
  font-style: italic;
  color: var(--color-text-secondary);
}

.rich-text :deep(ul),
.rich-text :deep(ol) {
  margin: 1rem 0 1.5rem 2rem;
}

.rich-text :deep(li) {
  margin-bottom: 0.5rem;
}

.rich-text :deep(ul li) {
  list-style: disc;
}

.rich-text :deep(ol li) {
  list-style: decimal;
}

.empty-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: var(--spacing-xl);
  color: var(--color-text-secondary);
}

.empty-content h2 {
  font-size: 1.5rem;
  font-weight: 600;
  margin: var(--spacing-lg) 0 var(--spacing-md) 0;
  color: var(--color-text-primary);
}

.empty-content p {
  margin-bottom: var(--spacing-sm);
  font-size: 14px;
}

.empty-subtitle {
  font-size: 12px !important;
  color: var(--color-text-muted);
}

.streaming-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: var(--spacing-xl);
  color: var(--color-text-primary);
}

.streaming-content h2 {
  font-size: 1.5rem;
  font-weight: 600;
  margin: var(--spacing-lg) 0 var(--spacing-md) 0;
  color: var(--color-text-primary);
}

.streaming-content p {
  margin-bottom: var(--spacing-sm);
  font-size: 14px;
  color: var(--color-text-secondary);
}

.streaming-icon :deep(svg) {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* ─── Editorial Letter Section Cards ─── */

.rich-text :deep(.letter-prose) {
  margin-bottom: 1.5rem;
}

.rich-text :deep(.letter-prose p) {
  margin-bottom: 1.25rem;
}

.rich-text :deep(.editorial-section) {
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 16px;
}

.rich-text :deep(.editorial-section__header) {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  cursor: pointer;
  user-select: none;
  gap: 12px;
}

.rich-text :deep(.editorial-section__header:hover) {
  filter: brightness(0.97);
}

.rich-text :deep(.editorial-section__header-left) {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.rich-text :deep(.editorial-section__badge) {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 3px 8px;
  border-radius: 4px;
  flex-shrink: 0;
  font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
}

.rich-text :deep(.editorial-section__title) {
  font-size: 17px;
  font-weight: 700;
  color: #24292f;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rich-text :deep(.section-chevron) {
  transition: transform 0.2s ease;
  flex-shrink: 0;
  color: #8b949e;
}

.rich-text :deep(.section-chevron.collapsed) {
  transform: rotate(-90deg);
}

.rich-text :deep(.editorial-section__body) {
  padding: 24px 28px 28px;
  font-family: 'PT Serif', serif;
  font-size: 16px;
  line-height: 1.8;
}

.rich-text :deep(.editorial-section.is-collapsed .editorial-section__body) {
  display: none;
}

.rich-text :deep(.editorial-section__body p) {
  margin-bottom: 1.1rem;
}

.rich-text :deep(.editorial-section__body p:last-child) {
  margin-bottom: 0;
}

.rich-text :deep(.editorial-section__body h1),
.rich-text :deep(.editorial-section__body h2),
.rich-text :deep(.editorial-section__body h3) {
  display: none; /* heading is shown in the card header instead */
}

.rich-text :deep(.editorial-section__body ol),
.rich-text :deep(.editorial-section__body ul) {
  padding-left: 24px;
  margin: 0.75rem 0;
}

.rich-text :deep(.editorial-section__body li) {
  margin-bottom: 10px;
}

/* Strength — green */
.rich-text :deep(.editorial-section--strength .editorial-section__header) {
  background: #dcf0e3;
  border-left: 5px solid #2d9e5f;
}
.rich-text :deep(.editorial-section--strength .editorial-section__badge) {
  background: #2d9e5f;
  color: #ffffff;
}
.rich-text :deep(.editorial-section--strength .editorial-section__body) {
  background: #f2faf5;
  border-left: 5px solid #2d9e5f;
}

/* Critical — dark blue */
.rich-text :deep(.editorial-section--critical .editorial-section__header) {
  background: #cfe3f1;
  border-left: 5px solid #1a5f8a;
}
.rich-text :deep(.editorial-section--critical .editorial-section__badge) {
  background: #1a5f8a;
  color: #ffffff;
}
.rich-text :deep(.editorial-section--critical .editorial-section__body) {
  background: #f0f7fc;
  border-left: 5px solid #1a5f8a;
}

/* Important — medium blue */
.rich-text :deep(.editorial-section--important .editorial-section__header) {
  background: #daeef8;
  border-left: 5px solid #61aed4;
}
.rich-text :deep(.editorial-section--important .editorial-section__badge) {
  background: #61aed4;
  color: #ffffff;
}
.rich-text :deep(.editorial-section--important .editorial-section__body) {
  background: #f5fafd;
  border-left: 5px solid #61aed4;
}

/* Strategic — muted steel blue */
.rich-text :deep(.editorial-section--strategic .editorial-section__header) {
  background: #f0f5f9;
  border-left: 5px solid #7aaec8;
}
.rich-text :deep(.editorial-section--strategic .editorial-section__badge) {
  background: #7aaec8;
  color: #ffffff;
}
.rich-text :deep(.editorial-section--strategic .editorial-section__body) {
  background: #f8f9fa;
  border-left: 5px solid #7aaec8;
}

.rich-text :deep(.editorial-section--strategic .editorial-section__body ol li::marker) {
  color: #7aaec8;
  font-weight: 700;
}

/* ─── Inline share cards ─── */

.rich-text :deep(.inline-share-card) {
  border-left: 4px solid #61aed4;
  border-radius: 8px;
  background: #f8fafc;
  margin: 20px 0 24px;
  padding: 16px 20px 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.rich-text :deep(.inline-share-card__quote) {
  font-family: 'PT Serif', serif;
  font-size: 15px;
  line-height: 1.65;
  color: #24292f;
  margin-bottom: 10px;
  quotes: '\201C' '\201D';
}

.rich-text :deep(.inline-share-card__quote::before) {
  content: open-quote;
  color: #8b949e;
  font-size: 1.1em;
}

.rich-text :deep(.inline-share-card__quote::after) {
  content: close-quote;
  color: #8b949e;
  font-size: 1.1em;
}

.rich-text :deep(.inline-share-card__actions) {
  display: flex;
  align-items: center;
  gap: 5px;
  justify-content: flex-end;
}

.rich-text :deep(.inline-share-label) {
  font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #8b949e;
  margin-right: 4px;
}

.rich-text :deep(.inline-share-btn) {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid #d0d7de;
  background: white;
  color: #57606a;
  cursor: pointer;
  padding: 0;
  transition:
    color 0.15s ease,
    border-color 0.15s ease;
}

.rich-text :deep(.inline-share-btn:hover) {
  background: #f6f8fa;
}

.rich-text :deep(.inline-share-btn[data-platform='x']:hover) {
  color: #000;
  border-color: #000;
}
.rich-text :deep(.inline-share-btn[data-platform='threads']:hover) {
  color: #000;
  border-color: #000;
}
.rich-text :deep(.inline-share-btn[data-platform='facebook']:hover) {
  color: #1877f2;
  border-color: #1877f2;
}
.rich-text :deep(.inline-share-btn[data-platform='linkedin']:hover) {
  color: #0a66c2;
  border-color: #0a66c2;
}
.rich-text :deep(.inline-share-btn[data-platform='copy']:hover),
.rich-text :deep(.inline-share-btn.copied) {
  color: #2d9e5f;
  border-color: #2d9e5f;
}
</style>
